import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Button } from '@uhg-abyss/web/ui/Button';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useTIN } from '@src/state/taxInformation';
import { useOnloading } from '@src/state/landing';
import { PageLayout } from '../PageLayout';
import { FLOW_ID } from '../AppConstants';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { adobeInteraction } from '../helpers';

const CancelLink = styled('p', {
  color: '$info1',
  fontWeight: 'bolder',
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'none',
    color: '$info1',
  },
});

const SaveProgressLink = styled('p', {
  color: '$info1',
  display: 'none',
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'none',
    color: '$info1',
  },
});

const OrgName = styled('p', {
  fontSize: '16px',
  lineHeight: 'normal',
  fontWeight: 900,
  marginBottom: '0px',
  fontFamily: '$primary',
});

const TaxIDLabel = styled('p', {
  fontSize: '16px',
  fontWeight: 700,
  fontFamily: '$primary',
});

const Container = styled('div', {
  display: 'flex',
  justifyContent: 'center',
});

const LeftColumn = styled('div', {
  width: '250px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'start',
});

const RightColumn = styled('div', {
  width: '250px',
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'right',
  justifyContent: 'start',
  zIndex: -1,
});
const MiddleColumn = styled('div', {
  width: '675px',
});
export const UserRegistrationShell = ({
  children,
  showProgressLink,
  showCancelLink,
  activeStep,
  showOnlyOrgAndTin,
}) => {
  const router = useRouter();
  const { token } = useParams();
  const { TINNumber, TINInfo } = useTIN.state();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const { currentFlow } = useOnloading.state();
  const { corpName } = TINInfo || {
    corpName: '',
  };

  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');

  return (
    <PageLayout style={{ paddingTop: '50px' }}>
      <Container>
        <LeftColumn>
          {showCancelLink || showOnlyOrgAndTin ? (
            <React.Fragment>
              {currentFlow === FLOW_ID.PROVIDER ||
              currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER ||
              currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN ? (
                <OrgName>{corpName}</OrgName>
              ) : null}
              <TaxIDLabel>Tax ID #: {TINNumber}</TaxIDLabel>
              {!showOnlyOrgAndTin && (
                <CancelLink
                  onClick={() => {
                    setCancelModalOpen(true);
                    adobeInteraction(
                      activeStep,
                      'cancel registration',
                      currentFlow,
                      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active
                    );
                  }}
                >
                  <strong>Cancel Registration</strong>
                </CancelLink>
              )}
            </React.Fragment>
          ) : null}
        </LeftColumn>

        <MiddleColumn>{children}</MiddleColumn>
        <RightColumn>
          {showProgressLink ? (
            <SaveProgressLink>
              <strong>Save Progress for Later</strong>
            </SaveProgressLink>
          ) : null}
        </RightColumn>
      </Container>
      <Modal
        size="md"
        isOpen={cancelModalOpen}
        onClose={() => {
          return setCancelModalOpen(false);
        }}
        title="Are you sure you want to cancel?"
        footer={
          <Modal.Section>
            <Layout.Group alignLayout="right">
              <Button
                variant="outline"
                onClick={() => {
                  return setCancelModalOpen(false);
                }}
              >
                No, Continue
              </Button>
              <Button
                onClick={() => {
                  return router.navigate(`/cancel_registration/${token}`);
                }}
              >
                Yes, Cancel
              </Button>
            </Layout.Group>
          </Modal.Section>
        }
      >
        <Modal.Section>
          <p>
            If you cancel, your changes will <strong>not</strong> be saved.
          </p>
        </Modal.Section>
      </Modal>
    </PageLayout>
  );
};

UserRegistrationShell.propTypes = {
  children: PropTypes.node,
  showProgressLink: PropTypes.bool,
  showCancelLink: PropTypes.bool,
  activeStep: PropTypes.string,
  showOnlyOrgAndTin: PropTypes.bool,
};

UserRegistrationShell.defaultProps = {
  children: null,
  showProgressLink: false,
  showCancelLink: true,
  showOnlyOrgAndTin: false,
  activeStep: '',
};
